<script setup lang="ts">
  import { ref, onMounted, onBeforeUnmount, onUnmounted, computed } from 'vue'
  import VueDropzone from 'vue2-dropzone-vue3'
  import { useClientActionStore } from '@/store/clientActionStore'
  import axios from 'axios'
  import type { ClientPortalCaseSchema } from '@/types'
  import { useToast } from '@/components/ui/toast'
  import { useThemeColor } from '@/composables/useThemeColor'
  import { useFormField } from '@/composables/useFormField'
  import {
    type ClientAiAnalysis as AiAnalysis,
    AiAnalysisService,
    type PaginatedTAiAnalysisList
  } from '@/types/generated'

  const clientActionStore = useClientActionStore()
  const API_URL = import.meta.env.VITE_API_URL
  const submitFileUrl = `${API_URL}v2/client/portal/questionnaires/submission/${clientActionStore.currentSubmissionId}/file`

  const { toast } = useToast()
  const emit = defineEmits(['change', 'update:modelValue', 'filesChanged'])
  const themeColor = computed(() => useThemeColor().value)

  interface VueDropzoneFiles {
    manuallyAddFile(file: { size: number; name: string; type: string }, url: string): void
    removeFile(file: any): void
  }

  interface UploadError {
    status?: number
    message: string
    file?: any
  }

  const myVueDropzone = ref<VueDropzoneFiles | null>(null)
  const errors = ref<UploadError[]>([])
  const isUploading = ref(false)
  const isDestroying = ref(false)

  const props = defineProps({
    field: {
      type: Object,
      default: () => ({})
    },
    modelValue: {
      type: Array as () => any[],
      default: () => []
    },
    isPreviewMode: {
      type: Boolean,
      default: false
    }
  })

  // Use the existing form field composable to handle validation
  const { selectedValue } = useFormField(props)

  const getCsrfToken = () => {
    const csrfToken = document.cookie
      .split('; ')
      .find((cookie) => cookie.startsWith('csrftoken='))
      ?.split('=')[1]

    return csrfToken || ''
  }

  const dropzoneOptions = ref({
    url: submitFileUrl,
    thumbnailWidth: 200,
    thumbnailHeight: 200,
    maxFilesize: 100,
    headers: {
      'X-CSRFToken': getCsrfToken(),
      'X-Access-Token': clientActionStore.getToken
    },
    addRemoveLinks: true,
    withCredentials: true,
    destroyDropzone: false,
    acceptedFiles: 'application/pdf, image/png, image/jpeg, image/jpg',
    dictDefaultMessage: 'Ziehe die Dateien hier hin, um sie hochzuladen',
    dictRemoveFile: 'Datei löschen',
    dictInvalidFileType: 'Dieser Dateityp wird nicht unterstützt',
    dictFileTooBig:
      'Die Datei ist zu groß ({{filesize}}MB). Maximale Dateigröße: {{maxFilesize}}MB',
    dictResponseError: 'Server antwortete mit {{statusCode}} code',
    dictCancelUpload: 'Upload abbrechen',
    dictUploadCanceled: 'Upload abgebrochen',
    dictMaxFilesExceeded: 'Keine weiteren Dateien möglich'
  })

  function findSchemaItem(data: ClientPortalCaseSchema, shortId: string, fieldLabel: string) {
    for (let submission of data.submissions) {
      if (submission.shortId === shortId) {
        if (submission.schema_ && Array.isArray(submission.schema_)) {
          for (let [pageIndex, page] of submission.schema_.entries()) {
            for (let [itemIndex, item] of page.items.entries()) {
              if (
                item.label === fieldLabel &&
                (item.type === 'filefield' || item.type === 'multifilefield')
              ) {
                return { page: pageIndex, index: itemIndex }
              }
            }
          }
        }
      }
    }
    return null
  }

  const startsAiAnalysis = computed(() => {
    for (let submission of data.submissions) {
      if (submission.shortId === clientActionStore.currentSubmissionId) {
        if (submission.schema_ && Array.isArray(submission.schema_)) {
          for (let [_, page] of submission.schema_.entries()) {
            for (let [_, item] of page.items.entries()) {
              if (item.uuid === props.field.uuid) {
                return item.starts_ai_analysis
              }
            }
          }
        }
      }
    }
    return false
  })

  const data: ClientPortalCaseSchema = clientActionStore.getClientAction!
  const uploadMeta = findSchemaItem(data, clientActionStore.currentSubmissionId, props.field.label)

  function handleError(error: UploadError) {
    errors.value.push(error)
    toast({
      title: 'Upload Fehler',
      description: error.message,
      variant: 'destructive',
      duration: 2000
    })
  }

  function handleUploadError(file: any, message: string, xhr: XMLHttpRequest) {
    isUploading.value = false
    let errorMessage = message

    try {
      if (xhr?.responseText) {
        const response = JSON.parse(xhr.responseText)
        errorMessage = response.detail || response.message || message
      }
    } catch (e) {
      console.error('Error parsing error response:', e)
    }

    handleError({
      status: xhr?.status,
      message: errorMessage,
      file: file
    })

    myVueDropzone.value?.removeFile(file)
  }

  function handleFileSizeError(file: any) {
    handleError({
      message: `Die Datei "${file.name}" ist zu groß. Maximale Größe: ${dropzoneOptions.value.maxFilesize}MB`,
      file: file
    })
  }

  function handleFileTypeError(file: any) {
    handleError({
      message: `Der Dateityp von "${file.name}" wird nicht unterstützt. Erlaubte Typen: PDF, PNG, JPEG`,
      file: file
    })
  }

  function sendingEvent(file: any, xhr: any, formData: any) {
    isUploading.value = true
    errors.value = errors.value.filter((error) => error.file !== file)

    if (!uploadMeta) {
      handleError({
        message: 'Fehler bei der Initialisierung des Uploads',
        file: file
      })
      return false
    }

    formData.append('page_index', uploadMeta.page)
    formData.append('item_index', uploadMeta.index)

    xhr.upload.addEventListener('progress', (e: ProgressEvent) => {
      if (e.lengthComputable) {
        const percentComplete = (e.loaded / e.total) * 100
        file.upload.progress = percentComplete
      }
    })

    xhr.onreadystatechange = function () {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        isUploading.value = false

        if (xhr.status !== 200) {
          let errorMessage = 'Fehler beim Hochladen der Datei'

          try {
            if (xhr.responseText) {
              const response = JSON.parse(xhr.responseText)
              errorMessage = response.detail || response.message || errorMessage
            }
          } catch (e) {
            console.error('Error parsing response:', e)
          }

          handleError({
            status: xhr.status,
            message: errorMessage,
            file: file
          })

          myVueDropzone.value?.removeFile(file)
        }
      }
    }
  }

  const aiAnalysis = ref<AiAnalysis[]>([]) // Reactive state for AI analysis data
  let analysisCheckInterval: ReturnType<typeof setInterval> | null = null // Use browser's setInterval and clearInterval

  // This function is triggered when a file upload or some other process succeeds
  function handleSuccess(file: any, response: any) {
    isUploading.value = false
    errors.value = errors.value.filter((error) => error.file !== file)

    try {
      file.uuid = response.uuid
      file.url = response.url
      file.dataURL = ''

      const updatedModelValue = [
        ...props.modelValue,
        {
          uuid: response.uuid,
          url: response.url,
          filename: response.filename,
          title: response.title
        }
      ]

      // Update the model value, which will trigger the useFormField validation
      emit('update:modelValue', updatedModelValue)
      emit('change', [...props.modelValue, file])
      emit('filesChanged')

      // Update selectedValue to match the model value
      selectedValue.value = updatedModelValue

      toast({
        title: 'Erfolg',
        description: `${file.name} wurde erfolgreich hochgeladen`,
        variant: 'default',
        duration: 2000
      })

      // Check if AI analysis should start
      if (!startsAiAnalysis.value) {
        return
      }

      // Step 1: Notify the user that AI analysis is starting
      toast({
        title: 'KI-Analyse',
        description: 'Das hochgeladene Dokument wird nun analysiert.',
        variant: 'default'
      })

      // Step 2: Fetch initial AI analysis data
      AiAnalysisService.aiAnalysisByDocumentUuidList({ documentUuid: file.uuid })
        .then((analysisResponse: PaginatedTAiAnalysisList) => {
          aiAnalysis.value = analysisResponse.results

          // Step 3: Start polling the AI analysis status
          analysisCheckInterval = setInterval(async () => {
            try {
              // Step 3a: Await the API call and store the updated analysis data
              const updatedResponse = await AiAnalysisService.aiAnalysisByDocumentUuidList({
                documentUuid: file.uuid
              })
              const updatedAnalysis = updatedResponse.results

              // Update the reactive aiAnalysis array
              aiAnalysis.value = updatedAnalysis

              // Step 3b: Check if all entries in aiAnalysis have is_finished = true
              const allFinished = aiAnalysis.value.every((analysis) => analysis.is_finished)

              if (allFinished) {
                // Step 3c: If all items are completed, stop polling and notify the user
                clearInterval(analysisCheckInterval as unknown as number)
                analysisCheckInterval = null

                toast({
                  title: 'KI-Analyse',
                  description: 'Das Dokument wurde erfolgreich analysiert.',
                  variant: 'success'
                })

                window.location.reload()
              }
            } catch (error) {
              console.error('Polling error:', error)

              // Handle errors, stop polling, and show an error toast
              clearInterval(analysisCheckInterval as unknown as number)
              analysisCheckInterval = null

              toast({
                title: 'KI-Analyse',
                description: 'Leider ist ein unerwarteter Fehler aufgetreten.',
                variant: 'destructive'
              })
            }
          }, 1000) // Poll every 1 second
        })
        .catch((error) => {
          console.error('Initial analysis fetch error:', error)

          // Handle the initial analysis service failure
          toast({
            title: 'KI-Analyse',
            description: 'Leider ist ein unerwarteter Fehler aufgetreten.',
            variant: 'destructive'
          })
        })
    } catch (e) {
      console.error('Error processing successful upload:', e)
      handleError({
        message: 'Fehler beim Verarbeiten der hochgeladenen Datei',
        file: file
      })
    }
  }

  function handleFileRemoved(file: any) {
    if (isDestroying.value) {
      return
    }

    if (file.status !== 'success') {
      const newModelValue = props.modelValue.filter((item: any) => item !== file)
      emit('update:modelValue', newModelValue)
      emit('change', newModelValue)
      emit('filesChanged')
      return
    }

    const uuid = file.uuid || (file.upload && file.upload.uuid)
    if (!uuid) {
      return
    }

    axios
      .delete(submitFileUrl, {
        headers: {
          'X-CSRFToken': getCsrfToken(),
          'X-Access-Token': clientActionStore.getToken
        },
        data: { uuid }
      })
      .then((response) => {
        // File deletion successful
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.detail || 'Fehler beim Löschen der Datei'
        toast({
          title: 'Fehler',
          description: errorMessage,
          variant: 'destructive',
          duration: 2000
        })
        console.error('File deletion error:', error)
      })

    const newModelValue = props.modelValue.filter((item: any) => item.uuid !== uuid)

    // Update the model value, which will trigger the useFormField validation
    emit('update:modelValue', newModelValue)
    emit('change', newModelValue)
    emit('filesChanged')

    // Update selectedValue to match the model value
    selectedValue.value = newModelValue
  }

  onBeforeUnmount(() => {
    isDestroying.value = true

    // Clean up any polling intervals
    if (analysisCheckInterval) {
      clearInterval(analysisCheckInterval)
      analysisCheckInterval = null
    }
  })

  onUnmounted(() => {
    isDestroying.value = false
  })

  onMounted(() => {
    if (props.modelValue?.length) {
      props.modelValue.forEach((file) => {
        const staticFile = {
          size: file.upload?.total || file.size,
          name: file.upload?.filename || file.filename,
          type: file.type,
          uuid: file.uuid
        }
        myVueDropzone.value?.manuallyAddFile(staticFile, file.url)
      })

      // If files are already present, ensure selectedValue is set correctly
      selectedValue.value = props.modelValue
    }

    const updatePreviewStyles = () => {
      document.querySelectorAll('.dz-preview .dz-details').forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.add(`bg-${themeColor.value}-600`, 'rounded-lg')
        }
      })
    }

    updatePreviewStyles()

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement && node.classList.contains('dz-preview')) {
            const details = node.querySelector('.dz-details')
            if (details instanceof HTMLElement) {
              details.classList.add(`bg-${themeColor.value}-600`, 'rounded-lg')
            }
          }
        })
      })
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true
    })
  })
</script>

<template>
  <div class="relative">
    <template v-if="!isPreviewMode">
      <VueDropzone
        id="dropzone"
        ref="myVueDropzone"
        :options="dropzoneOptions"
        class="flex h-full w-full flex-wrap items-center justify-center"
        @vdropzone-success="handleSuccess"
        @vdropzone-error="handleUploadError"
        @vdropzone-file-too-big="handleFileSizeError"
        @vdropzone-file-type-not-allowed="handleFileTypeError"
        @vdropzone-sending="sendingEvent"
        @vdropzone-removed-file="handleFileRemoved"
      />
      <div
        v-if="isUploading"
        class="absolute bottom-2 right-2 rounded bg-gray-800 px-3 py-1 text-sm text-white"
      >
        Hochladen...
      </div>
    </template>
    <div
      v-else
      class="flex h-full w-full flex-wrap items-center justify-center rounded-lg border-2 border-dashed
        border-gray-300 p-4"
    >
      <p class="text-center font-semibold text-red-600">
        Das Hochladen von Dokumenten ist in der Vorschau deaktiviert
      </p>
    </div>
  </div>
</template>

<style>
  .dropzone {
    min-height: 150px;
    border: 2px dashed #e2e8f0;
    border-radius: 0.5rem;
    background: white;
  }

  .dropzone .dz-message {
    margin: 2em 0;
  }

  .dropzone .dz-preview {
    margin: 1rem;
  }

  .dropzone .dz-preview .dz-details {
    padding: 1rem;
  }

  .dropzone .dz-preview .dz-success-mark,
  .dropzone .dz-preview .dz-error-mark {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    z-index: 500;
  }

  .dropzone .dz-preview .dz-error-message {
    top: 150px;
  }

  .dropzone .dz-preview .dz-progress {
    margin-top: 1rem;
  }
</style>
